import { useEffect, useRef } from 'react';
import { useStore, type ReactFlowState } from '@xyflow/react';

interface AlignmentGuidesProps {
  horizontal?: number;
  vertical?: number;
}

const storeSelector = (state: ReactFlowState) => ({
  width: state.width,
  height: state.height,
  transform: state.transform,
});

/**
 * Desenha linhas-guia (canvas overlay) nas coordenadas informadas, respeitando
 * o transform (pan/zoom) do React Flow. Baseado no exemplo oficial "Helper Lines".
 */
export function AlignmentGuides({ horizontal, vertical }: AlignmentGuidesProps) {
  const { width, height, transform } = useStore(storeSelector);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpi = window.devicePixelRatio || 1;
    canvas.width = width * dpi;
    canvas.height = height * dpi;
    ctx.scale(dpi, dpi);
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = '#ec4899'; // magenta/pink, padrão das ferramentas top
    ctx.lineWidth = 1;

    const [tx, ty, scale] = transform;

    if (typeof vertical === 'number') {
      const x = vertical * scale + tx;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    if (typeof horizontal === 'number') {
      const y = horizontal * scale + ty;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height, transform, horizontal, vertical]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute left-0 top-0 z-10"
      style={{ width, height }}
    />
  );
}
